import { z } from 'zod';

import type { Order, OrderCreateInput, OrderItem, OrderLineInput, OrderUpdateInput } from './api';
import { positionQuantityFieldSchema } from './menu-line-form-schema';

const orderLineFormSchema = z.object({
  positionId: z.string().uuid(),
  quantity: positionQuantityFieldSchema,
  notes: z.string().trim().max(1000),
});

export type OrderLineFormValues = z.infer<typeof orderLineFormSchema>;

function hasAnySelectedQuantity(items: OrderLineFormValues[]): boolean {
  return items.some((item) => item.quantity !== '' && Number(item.quantity) > 0);
}

/**
 * Mirrors `apps/api` `createOrderSchema`/`updateOrderSchema`
 * (`order.dto.ts`), same convention as `menuCalendarEntryFormSchema`
 * (`orders-module/tasks.md` decision 18: `@catering/shared-types` is not
 * touched by this change). `customerId`/`siteId`/`serviceDate` stay in
 * the schema so the edit form can render them read-only, but
 * `toUpdateInput` never sends them — `OrderUpdateInput` has no fields
 * for them at all, they are frozen once the order exists.
 *
 * `items` is one row per position with a published dish that day
 * (`rotating-menu/design.md` §4) rather than a free-form list: rows are
 * synced to the day's calendar by `OrderForm`, never added/removed by
 * the user. "Agregá al menos una línea" is enforced by requiring at
 * least one row with a quantity greater than zero — an empty `items`
 * array (no position has a published dish yet) does not fail this
 * check on its own, `OrderForm` shows its own guidance message instead.
 */
export const orderFormSchema = z
  .object({
    customerId: z.string().uuid({ message: 'Seleccioná una empresa' }),
    siteId: z.string().uuid({ message: 'Seleccioná una sede' }),
    // Empty unless the customer holds contracts in several points of sale (`OrderForm` decides).
    operatingUnitId: z.string(),
    serviceDate: z.string().trim().min(1, 'La fecha de servicio es obligatoria'),
    items: z.array(orderLineFormSchema),
    clientNotes: z.string().trim().max(2000),
    internalNotes: z.string().trim().max(2000),
    cutoffOverrideReason: z.string().trim().max(500),
  })
  .superRefine((data, ctx) => {
    if (data.items.length > 0 && !hasAnySelectedQuantity(data.items)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Agregá al menos una línea',
        path: ['items'],
      });
    }
  });

export type OrderFormValues = z.infer<typeof orderFormSchema>;

export const DEFAULT_ORDER_FORM_VALUES: OrderFormValues = {
  customerId: '',
  siteId: '',
  operatingUnitId: '',
  serviceDate: '',
  items: [],
  clientNotes: '',
  internalNotes: '',
  cutoffOverrideReason: '',
};

/**
 * Only items with a frozen `positionId` become editable rows
 * (`rotating-menu/design.md` §1.4, T6). Legacy items with no position
 * (`positionId === null`) predate this migration and are not
 * addressable by any position row — `legacyOrderItems` below surfaces
 * them separately, read-only.
 */
export function toOrderFormValues(order: Order): OrderFormValues {
  return {
    customerId: order.customerId,
    siteId: order.siteId,
    operatingUnitId: order.operatingUnitId ?? '',
    serviceDate: order.serviceDate,
    items: order.items
      .filter((item): item is OrderItem & { positionId: string } => item.positionId !== null)
      .map((item) => ({
        positionId: item.positionId,
        quantity: String(item.quantity),
        notes: item.notes ?? '',
      })),
    clientNotes: order.clientNotes ?? '',
    internalNotes: order.internalNotes ?? '',
    cutoffOverrideReason: order.cutoffOverrideReason ?? '',
  };
}

/**
 * Items frozen before `rotating-menu` with no `positionId` to edit —
 * shown read-only by `OrderForm` in edit mode and never resent: mapping
 * them by `menuId` to today's position risks silently swapping a frozen
 * historical line for a different position, so this is the simpler,
 * safer option (`tasks.md` PR 10 scope note).
 */
export function legacyOrderItems(order: Order): OrderItem[] {
  return order.items.filter((item) => item.positionId === null);
}

function toLineInputs(items: OrderLineFormValues[]): OrderLineInput[] {
  return items
    .filter((item) => item.quantity !== '' && Number(item.quantity) > 0)
    .map((item) => ({
      positionId: item.positionId,
      quantity: Number(item.quantity),
      notes: item.notes === '' ? null : item.notes,
    }));
}

export function toOrderCreateInput(values: OrderFormValues): OrderCreateInput {
  return {
    customerId: values.customerId,
    siteId: values.siteId,
    ...(values.operatingUnitId === '' ? {} : { operatingUnitId: values.operatingUnitId }),
    serviceDate: values.serviceDate,
    items: toLineInputs(values.items),
    clientNotes: values.clientNotes === '' ? null : values.clientNotes,
    internalNotes: values.internalNotes === '' ? null : values.internalNotes,
    ...(values.cutoffOverrideReason === ''
      ? {}
      : { cutoffOverrideReason: values.cutoffOverrideReason }),
  };
}

export function toOrderUpdateInput(values: OrderFormValues): OrderUpdateInput {
  return {
    items: toLineInputs(values.items),
    clientNotes: values.clientNotes === '' ? null : values.clientNotes,
    internalNotes: values.internalNotes === '' ? null : values.internalNotes,
    ...(values.cutoffOverrideReason === ''
      ? {}
      : { cutoffOverrideReason: values.cutoffOverrideReason }),
  };
}
